import React, { useEffect, useRef, useState } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  FolderAddOutlined,
  FolderOutlined,
  PushpinOutlined,
  RightOutlined
} from '@ant-design/icons';
import { Dropdown, MenuProps } from 'antd';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import OrbitThemeScope from '../../OrbitThemeScope';
import { TOrbit } from '../../i18n/TModules';
import { ORBIT_WORKSPACES } from '../../orbitWorkspaces';
import { orbitTokens } from '../../styles';
import { OrbitWorkspaceTag } from '../../types';
import OrbitWorkspaceSubmenu from './OrbitWorkspaceSubmenu';
import { PlainButton, type } from './pageStyles';

const Trigger = styled(PlainButton)<{ $glyph: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: ${orbitTokens.headerControlInk};
  font-size: ${({ $glyph }) => $glyph}px;
`;

// Figma 6519:8247: #15203E, r8, 0 10 34 black at 50%; each item a 16 icon at x 10 and its label at
// x 37 in #AEBBD0, at a 31 pitch. Literal colours, as the menu renders outside the page.
const MenuCard = styled.div`
  overflow: hidden;
  border-radius: 8px;
  background: #15203e;
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.5);
  font-family: ${orbitTokens.fontFamily};

  && .ant-dropdown-menu {
    padding: 4px 0;
    background: transparent;
    box-shadow: none;
  }

  && .ant-dropdown-menu-item {
    min-height: 31px;
    padding: 0 18px 0 10px;
    color: #aebbd0;
    ${type('containerBody')}
  }

  && .ant-dropdown-menu-item-icon {
    min-width: 16px;
    margin-right: 11px;
    font-size: 16px;
  }

  && .ant-dropdown-menu-item-active:not(.ant-dropdown-menu-item-disabled) {
    background: rgba(255, 255, 255, 0.06);
  }

  && .ant-dropdown-menu-item-disabled,
  && .ant-dropdown-menu-item-disabled:hover {
    color: #aebbd0;
    opacity: 0.45;
  }
`;

// The Workspace submenu hangs off the card, outside its rounded clip.
const MenuFrame = styled.div`
  position: relative;
`;

// 6690:3484: the Workspace item with a trailing chevron, the mark of a submenu.
const ItemLabel = styled.span`
  display: inline-flex;
  align-items: center;

  > * + * {
    margin-left: 8px;
  }

  .anticon {
    font-size: 10px;
  }
`;

/*
  Where each menu hangs (antd's own offset is 4 down). 6519:8247: the row card's top meets the 14
  glyph's bottom, 5 above the 24 trigger's, its left 5 left of the glyph like the trigger's.
  6519:8283: the header card is centred under the 24 dots, its top on their bottom.
  6690:3498: the side panel's card ends 4 right of its dots (483 against 479), its top on their
  bottom. 6690:3458 opens the row menu's submenu to its right; the header and the panel sit at the
  window's right edge, so theirs opens to the left.
*/
const ANCHORS = {
  row: { glyph: 14, placement: 'bottomLeft', offset: [0, -5], submenu: 'right' },
  header: { glyph: 24, placement: 'bottom', offset: [0, 0], submenu: 'left' },
  panel: { glyph: 24, placement: 'bottomRight', offset: [4, 0], submenu: 'left' }
} as const;

export interface IOrbitChatMenuWorkspace {
  /** The chat's Workspace; null while it is in Recent. */
  current: OrbitWorkspaceTag | null;
  /** Files the chat there, or back in Recent with `null`. */
  onChoose: (tag: OrbitWorkspaceTag | null) => void;
}

export interface IOrbitChatMenuPin {
  pinned: boolean;
  onToggle: () => void;
}

export interface IOrbitChatMenuProps {
  anchor: keyof typeof ANCHORS;
  /** The element naming the chat, so each row's trigger is told apart from the next. */
  describedBy?: string;
  /** Null for a chat the server cannot rename yet: Rename then renders disabled. */
  onRename: (() => void) | null;
  /** Null for a chat the server cannot pin yet: Pin then renders disabled. */
  pin: IOrbitChatMenuPin | null;
  /** Null for a chat with no saved session to delete: Delete then renders disabled. */
  onDelete: (() => void) | null;
  /** Null for a chat with nowhere to keep a Workspace: Add to Workspace then renders disabled. */
  workspace: IOrbitChatMenuWorkspace | null;
  testId: string;
}

/**
 * The chat "…" menu button: Rename, Pin or Unpin, Delete, and the chat's Workspace: Add to
 * Workspace › for a chat in Recent, Move to Workspace › and Remove from Workspace for a filed one.
 */
const OrbitChatMenu: React.FC<IOrbitChatMenuProps> = ({
  anchor,
  describedBy,
  onRename,
  pin,
  onDelete,
  workspace,
  testId
}) => {
  const { glyph, placement, offset, submenu } = ANCHORS[anchor];
  const { t } = useMultiLanguageTranslation();
  const [open, setOpen] = useState(false);
  const [submenuTop, setSubmenuTop] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const submenuLabelRef = useRef<HTMLSpanElement>(null);
  const filed = workspace !== null && workspace.current !== null;
  const submenuKey = filed ? 'moveToWorkspace' : 'addToWorkspace';
  const submenuLabel = t(`${TOrbit}.page.chatMenu.${submenuKey}`);

  const submenuRow = () =>
    submenuLabelRef.current?.closest<HTMLElement>('[role="menuitem"]') ?? null;

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSubmenuTop(null);
    }
  };

  // rc-dropdown's own `autoFocus`, and its Tab handling, throw on this rc-trigger version, so the
  // menu moves focus to its first enabled item itself.
  useEffect(() => {
    if (open) {
      listRef.current
        ?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')
        ?.focus();
    }
  }, [open]);

  const close = () => {
    changeOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      // Kept from rc-dropdown's window listener, which would handle the same key again.
      event.stopPropagation();
      close();
    }
  };

  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (open && event.key === 'Escape') {
      event.stopPropagation();
      changeOpen(false);
    }
  };

  const workspaceItems: NonNullable<MenuProps['items']> = [
    {
      key: submenuKey,
      icon: filed ? (
        <FolderOutlined aria-hidden="true" />
      ) : (
        <FolderAddOutlined aria-hidden="true" />
      ),
      label: (
        <ItemLabel ref={submenuLabelRef}>
          <span>{submenuLabel}</span>
          <RightOutlined aria-hidden="true" />
        </ItemLabel>
      ),
      disabled: workspace === null,
      // rc-menu passes these on to the item's <li>.
      ...({ 'aria-haspopup': 'menu', 'aria-expanded': submenuTop !== null } as object)
    },
    ...(filed
      ? [
          {
            key: 'removeFromWorkspace',
            // 6703:5758 draws the folder glyph here too.
            icon: <FolderOutlined aria-hidden="true" />,
            label: t(`${TOrbit}.page.chatMenu.removeFromWorkspace`)
          }
        ]
      : [])
  ];

  const items: MenuProps['items'] = [
    {
      key: 'rename',
      icon: <EditOutlined aria-hidden="true" />,
      label: t(`${TOrbit}.page.chatMenu.rename`),
      disabled: onRename === null
    },
    {
      key: 'pin',
      icon: <PushpinOutlined aria-hidden="true" />,
      label: t(`${TOrbit}.page.chatMenu.${pin?.pinned ? 'unpin' : 'pin'}`),
      disabled: pin === null
    },
    {
      key: 'delete',
      icon: <DeleteOutlined aria-hidden="true" />,
      label: t(`${TOrbit}.page.chatMenu.delete`),
      disabled: onDelete === null
    },
    ...workspaceItems
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === submenuKey) {
      const rowTop = submenuRow()?.getBoundingClientRect().top ?? 0;
      setSubmenuTop(rowTop - (frameRef.current?.getBoundingClientRect().top ?? 0));
      return;
    }
    // The dialog hands focus back to whatever held it when it opened: the trigger, not a menu item
    // that is about to hide.
    close();
    if (key === 'rename') {
      onRename?.();
    }
    if (key === 'pin') {
      pin?.onToggle();
    }
    if (key === 'delete') {
      onDelete?.();
    }
    if (key === 'removeFromWorkspace') {
      workspace?.onChoose(null);
    }
  };

  return (
    <Dropdown
      trigger={['click']}
      placement={placement}
      align={{ offset: [...offset] }}
      open={open}
      onOpenChange={changeOpen}
      menu={{ items, onClick }}
      dropdownRender={menu => (
        // Portalled to `document.body`, so it re-declares the tokens the card reads.
        <OrbitThemeScope>
          <MenuFrame ref={frameRef}>
            <MenuCard ref={listRef} onKeyDown={onMenuKeyDown} data-testid={`${testId}-list`}>
              {menu}
            </MenuCard>
            {workspace !== null && submenuTop !== null ? (
              <OrbitWorkspaceSubmenu
                label={submenuLabel}
                options={ORBIT_WORKSPACES.filter(option => option.tag !== workspace.current)}
                side={submenu}
                top={submenuTop}
                onChoose={tag => {
                  close();
                  workspace.onChoose(tag);
                }}
                onBack={() => {
                  setSubmenuTop(null);
                  submenuRow()?.focus();
                }}
                onLeave={close}
                testId={`${testId}-workspaces`}
              />
            ) : null}
          </MenuFrame>
        </OrbitThemeScope>
      )}
    >
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={t(`${TOrbit}.page.chatMenu.label`)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-describedby={describedBy}
        data-testid={testId}
        $glyph={glyph}
        onKeyDown={onTriggerKeyDown}
      >
        <EllipsisOutlined aria-hidden="true" />
      </Trigger>
    </Dropdown>
  );
};

export default OrbitChatMenu;
